import { type FC, useEffect, useState } from 'react';
import { useAppContext } from '../../core/context';
import { useFlag } from '../../feature-flags';
import { useApi } from '../../platform/api/useApi';
import { useLanguageContext } from '../../core/locale/LanguageContext';
import Separator from '../Separator/Separator';
import './QrBlock.css';

type QrState = { status: 'loading' } | { status: 'ready'; url: string } | { status: 'unavailable' };

const QrBlock: FC = () => {
  const { apiAuth, apiSubscriptionId } = useAppContext();
  const showNewDesign = useFlag('redesign');
  const { getQr } = useApi();
  const languageContext = useLanguageContext();
  const [qrState, setQrState] = useState<QrState>({ status: 'loading' });

  useEffect(() => {
    let isCancelled = false;

    const fetchQr = async () => {
      const response = await getQr({ apiSubscriptionId, token: apiAuth });
      if (isCancelled) {
        return;
      }

      const url = response?.url ?? '';
      setQrState(url ? { status: 'ready', url } : { status: 'unavailable' });
    };

    void fetchQr();

    return () => {
      isCancelled = true;
    };
  }, [apiAuth, apiSubscriptionId, getQr]);

  if (qrState.status === 'unavailable') {
    return null;
  }

  return (
    <div className="qr-block">
      <h2 className="qr-block-title">{languageContext.getLocalizedText('scanToPay')}</h2>
      <div className={'qr-wrapper'}>
        {qrState.status === 'loading' ?
          <div className="qr-image qr-placeholder" aria-hidden="true" />
        : <img
            className="qr-image"
            alt={languageContext.getLocalizedText('qrAltTag')}
            src={qrState.url}
            referrerPolicy="no-referrer"
            onError={() => {
              setQrState({ status: 'unavailable' });
            }}
          />
        }
      </div>
      {!showNewDesign && <Separator text={languageContext.getLocalizedText('qrSeparator')} />}
    </div>
  );
};

export default QrBlock;
